import { useEffect, useRef } from "react";
import { isTypingTarget } from "@src/common/utils/focusUtils";
import { useShipmentSelectionContext } from "../context/ShipmentSelectionContext";
import { useModalsContext } from "../context/ShipmentsTableModalsContext";
import { useBulkShipmentActions } from "../hooks/useBulkShipmentActions";

// While shipments are checked, pressing a bound letter runs the matching bulk
// action (e.g. "A" → Assign Driver). Mirrors ShipmentRowShortcutListener but
// targets the whole selection via useBulkShipmentActions. Renders nothing.
export function BulkShipmentShortcutListener() {
  const { checkedShipments, isBulkActionPaletteOpen } =
    useShipmentSelectionContext();
  const { openedModalName } = useModalsContext();
  const { actions } = useBulkShipmentActions();

  const stateRef = useRef({
    actions,
    checkedShipments,
    openedModalName,
    isBulkActionPaletteOpen,
  });
  stateRef.current = {
    actions,
    checkedShipments,
    openedModalName,
    isBulkActionPaletteOpen,
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const { actions, checkedShipments, openedModalName, isBulkActionPaletteOpen } =
        stateRef.current;

      // Let browser/OS shortcuts pass through untouched.
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      // Don't hijack typing in inputs or editable fields. Focused row
      // checkboxes are exempt so clicking one doesn't block shortcuts.
      if (isTypingTarget(document.activeElement)) return;

      // A modal owns the keyboard while open; the palette has its own handling.
      if (openedModalName) return;
      if (isBulkActionPaletteOpen) return;
      // Only act in bulk mode.
      if (checkedShipments.length === 0) return;

      const key = event.key.toLowerCase();
      const action = actions.find(
        (a) => a.shortcut?.toLowerCase() === key && a.show && !a.disabled
      );
      if (!action) return;

      event.preventDefault();
      action.onClick();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return null;
}

export default BulkShipmentShortcutListener;
